import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../constants';
import { ROUTES } from '../../../routes';
import SEO from '../../shared/SEO';
import SkillDetailHeader from './SkillDetailHeader';
import SkillEffectsList from './SkillEffectsList';
import SkillCardsList from './SkillCardsList';
import SkillSupportList from './SkillSupportList';

/* ─── animation-designer: CSS keyframe animations sin Framer Motion ─────── */
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=JetBrains+Mono:wght@400;600&display=swap');

@keyframes sd-fade-up {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes sd-shimmer {
  0%   { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}
@keyframes sd-pulse-gold {
  0%, 100% { box-shadow: 0 0 0 0 rgba(234,179,8,0); }
  50%       { box-shadow: 0 0 12px 3px rgba(234,179,8,0.25); }
}
@keyframes sd-back-slide {
  from { transform: translateX(0); }
  to   { transform: translateX(-4px); }
}

.sd-page       { animation: sd-fade-up 0.45s ease-out both; }
.sd-card       { animation: sd-fade-up 0.45s ease-out both; }
.sd-card-delay { animation: sd-fade-up 0.45s 0.12s ease-out both; }

.sd-skeleton-line {
  background: linear-gradient(90deg,
    rgba(156,163,175,0.15) 25%,
    rgba(156,163,175,0.35) 50%,
    rgba(156,163,175,0.15) 75%);
  background-size: 600px 100%;
  animation: sd-shimmer 1.4s infinite linear;
  border-radius: 6px;
}
.dark .sd-skeleton-line {
  background: linear-gradient(90deg,
    rgba(75,85,99,0.3) 25%,
    rgba(75,85,99,0.55) 50%,
    rgba(75,85,99,0.3) 75%);
  background-size: 600px 100%;
}

/* Highlight ring for rarity-5 icons */
.sd-rarity-5-glow { animation: sd-pulse-gold 2.4s ease-in-out infinite; }

/* Back button arrow hover */
.sd-back-arrow:hover .sd-arrow-icon { animation: sd-back-slide 0.25s ease-out forwards; }
`;

/* ─── Skeleton Loader (animation-designer: anticipation + shimmer) ────────── */
const SkillDetailSkeleton = () => (
    <div className="space-y-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex gap-5">
                <div className="sd-skeleton-line w-20 h-20 rounded-xl flex-shrink-0" />
                <div className="flex-1 space-y-3 pt-1">
                    <div className="sd-skeleton-line h-7 w-2/3" />
                    <div className="sd-skeleton-line h-4 w-1/3" />
                    <div className="sd-skeleton-line h-5 w-28" />
                </div>
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2">
                <div className="sd-skeleton-line h-4 w-full" />
                <div className="sd-skeleton-line h-4 w-5/6" />
                <div className="sd-skeleton-line h-4 w-3/4" />
            </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 space-y-3">
            <div className="sd-skeleton-line h-3 w-32" />
            <div className="sd-skeleton-line h-10 w-full rounded-xl" />
            <div className="sd-skeleton-line h-10 w-full rounded-xl" />
        </div>
    </div>
);

/* ─── Error State ────────────────────────────────────────────────────────── */
const SkillDetailError = ({ message, onRetry }) => (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
        </div>
        <h3 className="text-red-800 dark:text-red-300 font-bold text-lg mb-1">Skill not found</h3>
        <p className="text-red-500 dark:text-red-400 text-sm mb-5">{message}</p>
        <button
            onClick={onRetry}
            className="px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm font-semibold"
        >
            Try again
        </button>
    </div>
);

/* ─── Main Component ─────────────────────────────────────────────────────── */
const SkillDetail = () => {
    const { id_skill } = useParams();
    const navigate = useNavigate();

    const [skill, setSkill] = useState(null);
    const [skillEffects, setSkillEffects] = useState([]);
    const [baseSkill, setBaseSkill] = useState(null);
    const [upgradeSkill, setUpgradeSkill] = useState(null);
    const [cards, setCards] = useState([]);
    const [supports, setSupports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchSkillDetail = useCallback(async () => {
        setLoading(true);
        setError(null);
        setBaseSkill(null);
        setUpgradeSkill(null);
        setCards([]);
        setSupports([]);
        try {
            const response = await fetch(`${API_BASE_URL}/api/skill/${id_skill}`);
            if (!response.ok) {
                if (response.status === 404) throw new Error(`Skill ${id_skill} does not exist.`);
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            setSkill(data.skill);
            setSkillEffects(data.skill_effects || []);

            const relatedFetches = [];
            if (data.skill.base_skill_id) relatedFetches.push({ key: 'base', id: data.skill.base_skill_id });
            if (data.skill.upgrade_id) relatedFetches.push({ key: 'upgrade', id: data.skill.upgrade_id });

            if (relatedFetches.length > 0) {
                const responses = await Promise.allSettled(
                    relatedFetches.map(({ id }) =>
                        fetch(`${API_BASE_URL}/api/skill/${id}`).then(r => r.ok ? r.json() : null)
                    )
                );
                responses.forEach((result, i) => {
                    if (result.status === 'fulfilled' && result.value?.skill) {
                        const s = result.value.skill;
                        const minimal = { Id_skill: s.Id_skill, Skill_name: s.Skill_name, icon_id: s.icon_id, rarity: s.rarity };
                        if (relatedFetches[i].key === 'base') setBaseSkill(minimal);
                        if (relatedFetches[i].key === 'upgrade') setUpgradeSkill(minimal);
                    }
                });
            }

            // Fetches paralelos de cards y supports (silencio si fallan)
            const [cardsRes, supportsRes] = await Promise.allSettled([
                fetch(`${API_BASE_URL}/api/skill-cards/${data.skill.Id}`).then(r => r.ok ? r.json() : null),
                fetch(`${API_BASE_URL}/api/skill-support/${data.skill.Id_skill}`).then(r => r.ok ? r.json() : null),
            ]);
            if (cardsRes.status === 'fulfilled' && cardsRes.value?.cards) {
                setCards(cardsRes.value.cards);
            }
            if (supportsRes.status === 'fulfilled' && supportsRes.value?.supports) {
                setSupports(supportsRes.value.supports);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id_skill]);

    useEffect(() => { fetchSkillDetail(); }, [fetchSkillDetail]);

    return (
        <>
            {/* Inject CSS animations & Google Fonts */}
            <style>{styles}</style>

            <div className="min-h-screen bg-panel-gray dark:bg-gray-900 p-4 sm:p-6">
                <div className="max-w-3xl mx-auto space-y-4 sd-page">
                    <SEO
                        title={skill ? `${skill.Skill_name} — Skill Detail` : 'Skill Detail'}
                        description={skill?.description || 'Skill detail page with effects and activation conditions.'}
                    />

                    {/* ── Back button — animation-designer: hover arrow slide ── */}
                    <button
                        onClick={() => navigate(ROUTES.SKILLS)}
                        className="sd-back-arrow group inline-flex items-center gap-2
                            px-4 py-2 rounded-xl text-sm font-semibold
                            bg-white dark:bg-gray-800
                            text-gray-600 dark:text-gray-300
                            border border-gray-200 dark:border-gray-700
                            hover:border-gray-300 dark:hover:border-gray-500
                            hover:text-gray-900 dark:hover:text-white
                            shadow-sm hover:shadow
                            transition-all duration-200"
                    >
                        <svg className="sd-arrow-icon w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Skills
                    </button>

                    {loading && <SkillDetailSkeleton />}

                    {!loading && error && <SkillDetailError message={error} onRetry={fetchSkillDetail} />}

                    {!loading && !error && skill && (
                        <>
                            {/* Hero panel */}
                            <div className="sd-card">
                                <SkillDetailHeader skill={skill} baseSkill={baseSkill} upgradeSkill={upgradeSkill} />
                            </div>

                            {/* Effects panel */}
                            <div className="sd-card-delay">
                                {skillEffects.length > 0 ? (
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                                        <SkillEffectsList skillEffects={skillEffects} />
                                    </div>
                                ) : (
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-8 text-center">
                                        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                                            No activation data available for this skill yet.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Cards y Support panel — solo se renderiza si hay datos */}
                            {(cards.length > 0 || supports.length > 0) && (
                                <div className="sd-card-delay bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 space-y-6">
                                    <SkillSupportList supports={supports} />
                                    <SkillCardsList cards={cards} />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default SkillDetail;
